import React, { useCallback } from 'react'
import { useQuery, useMutation } from '@apollo/client'
import { Modal, Spin, notification } from 'antd'
import { SCRAPING_TAG_GROUPS_OPTIONS } from '../../../graphql/queries'
import {
  CREATE_SCRAPING_SESSION_TEMPLATE,
  UPDATE_SCRAPING_SESSION_TEMPLATE,
} from '../../../graphql/mutations'
import { ScrapingSessionTemplateEditorForm } from '../../forms'
import PropTypes from 'prop-types'

const ScrapingSessionTemplateEditorModal = ({
  scrapingSessionTemplate,
  refetchScrapingSessionTemplates,
  isModalOpen,
  closeModal,
}) => {
  const {
    data: { scrapingTagGroupsOptions } = {},
    loading: isLoadingScrapingTagGroupsOptions,
  } = useQuery(SCRAPING_TAG_GROUPS_OPTIONS, {
    fetchPolicy: 'no-cache',
  })

  const [
    createScrapingSessionTemplate,
    { loading: isCreatingScrapingSessionTemplate },
  ] = useMutation(CREATE_SCRAPING_SESSION_TEMPLATE, {
    onCompleted() {
      refetchScrapingSessionTemplates()
      closeModal()
      notification.open({
        message: 'Created scraping session template.',
        type: 'success',
      })
    },
    onError({ message }) {
      notification.open({
        message: 'Failed to create scraping session template.',
        description: message,
        type: 'error',
        duration: 0,
      })
    },
  })

  const [
    updateScrapingSessionTemplate,
    { loading: isUpdatingScrapingSessionTemplate },
  ] = useMutation(UPDATE_SCRAPING_SESSION_TEMPLATE, {
    onCompleted() {
      refetchScrapingSessionTemplates()
      closeModal()
      notification.open({
        message: 'Updated scraping session template.',
        type: 'success',
      })
    },
    onError({ message }) {
      notification.open({
        message: 'Failed to update scraping session template.',
        description: message,
        type: 'error',
        duration: 0,
      })
    },
  })

  const handleSubmit = useCallback(
    variables =>
      variables.id
        ? updateScrapingSessionTemplate({ variables })
        : createScrapingSessionTemplate({ variables }),
    [createScrapingSessionTemplate, updateScrapingSessionTemplate],
  )

  return (
    <Modal
      width={1400}
      title={
        scrapingSessionTemplate
          ? 'Edit session template'
          : 'Create session template'
      }
      visible={isModalOpen}
      footer={null}
      onCancel={closeModal}
      destroyOnClose
    >
      {isLoadingScrapingTagGroupsOptions ? (
        <Spin tip="Loading..." />
      ) : (
        <ScrapingSessionTemplateEditorForm
          scrapingSessionTemplate={scrapingSessionTemplate}
          scrapingTagGroups={scrapingTagGroupsOptions}
          onSubmit={handleSubmit}
          isSubmitting={
            isCreatingScrapingSessionTemplate ||
            isUpdatingScrapingSessionTemplate
          }
        />
      )}
    </Modal>
  )
}

ScrapingSessionTemplateEditorModal.propTypes = {
  scrapingSessionTemplate: PropTypes.object,
  refetchScrapingSessionTemplates: PropTypes.func.isRequired,
  isModalOpen: PropTypes.bool.isRequired,
  closeModal: PropTypes.func.isRequired,
}

ScrapingSessionTemplateEditorModal.defaultProps = {
  scrapingSessionTemplate: undefined,
}
export default ScrapingSessionTemplateEditorModal
